import React, { useMemo, useReducer, useState } from 'react';
import Form from 'react-bootstrap/Form';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { MdArrowBack, MdKeyboardReturn, MdLibraryAdd } from 'react-icons/md';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from '../../toasts';
import { Button, EntityList, FormField, PageHead, Section, Select, controlClass } from '../../components/EntityList';
import { useEditorReturn } from '../../hooks/useEditorReturn';
import BankAccountService from '../../services/bankAccount.service';
import { getError } from '../../utils';

const CURRENCIES = ['USD', 'UYU'];
const ACCOUNT_TYPES = ['Cuenta Corriente', 'Caja de Ahorro'];

const reducer = (state, action) => {
	switch (action.type) {
	case 'CREATE_REQUEST':
		return { ...state, loadingCreate: true };
	case 'CREATE_SUCCESS':
		return { ...state, loadingCreate: false };
	case 'CREATE_FAIL':
		return { ...state, loadingCreate: false };
	default:
		return state;
	}
};

const REQUIRED_FIELD_MESSAGE = 'Este campo es obligatorio.';

const FIELD_LABELS = {
	owner: 'Titular',
	bank: 'Banco',
	accountType: 'Tipo de cuenta',
	accountNumber: 'Número de cuenta',
	currency: 'Moneda',
};

const FieldError = ({ message }) =>
	message ? <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{message}</p> : null;

FieldError.propTypes = {
	message: PropTypes.string,
};

function NewBankAccount() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { returnTo, onReturn, returnLabel } = useEditorReturn();
	// Which query key to write the created id onto (see the call site in
	// AccountEditor.jsx) — defaults to 'bankAccountId' for callers with a
	// single bank-account field (e.g. AssetSaleEditor.jsx) that never send it.
	const bankAccountIdKey = searchParams.get('bankAccountIdKey') || 'bankAccountId';
	const [owner, setOwner] = useState('');
	const [bank, setBank] = useState('');
	const [branch, setBranch] = useState('');
	const [accountType, setAccountType] = useState('');
	const [accountNumber, setAccountNumber] = useState('');
	// Locked when opened from AccountEditor.jsx's per-currency bank-account
	// fields (?currency=USD|UYU) — keeps the created record's currency from
	// drifting away from the field that requested it.
	const lockedCurrency = searchParams.get('currency');
	const isCurrencyLocked = CURRENCIES.includes(lockedCurrency);
	const [currency, setCurrency] = useState(() => (isCurrencyLocked ? lockedCurrency : ''));
	const [fieldErrors, setFieldErrors] = useState({});

	const [{ loadingCreate }, dispatch] = useReducer(reducer, {
		loadingCreate: false,
	});

	const currencyOptions = useMemo(() => CURRENCIES, []);
	const accountTypeOptions = useMemo(() => ACCOUNT_TYPES, []);
	const backTo = returnTo || '/AdminScreen';

	const clearFieldError = (field) => {
		setFieldErrors((prevErrors) => {
			if (!prevErrors[field]) return prevErrors;
			const nextErrors = { ...prevErrors };
			delete nextErrors[field];
			return nextErrors;
		});
	};

	const validate = () => {
		const errors = {};
		if (!owner.trim()) errors.owner = REQUIRED_FIELD_MESSAGE;
		if (!bank.trim()) errors.bank = REQUIRED_FIELD_MESSAGE;
		if (!accountType) errors.accountType = REQUIRED_FIELD_MESSAGE;
		if (!accountNumber.trim()) errors.accountNumber = REQUIRED_FIELD_MESSAGE;
		if (!currency) errors.currency = REQUIRED_FIELD_MESSAGE;
		return errors;
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (loadingCreate) {
			return;
		}

		const errors = validate();
		if (Object.keys(errors).length > 0) {
			setFieldErrors(errors);
			const missingLabels = Object.keys(errors).map((field) => FIELD_LABELS[field] || field);
			toast.error(`Complete el/los siguiente/s campo/s requerido/s: ${missingLabels.join(', ')}.`);
			return;
		}

		try {
			dispatch({ type: 'CREATE_REQUEST' });
			const response = await BankAccountService.create({
				owner: owner.trim(),
				bank: bank.trim(),
				branch: branch.trim(),
				accountType,
				accountNumber: accountNumber.trim(),
				currency,
			});
			const createdAccount = response.data;
			dispatch({ type: 'CREATE_SUCCESS' });
			setFieldErrors({});
			toast.success('Cuenta bancaria creada');

			if (returnTo && createdAccount) {
				const target = new URL(returnTo, window.location.origin);
				const createdBankAccountId = createdAccount._id || createdAccount.id;
				if (createdBankAccountId) {
					// Callers with more than one bank-account field (e.g.
					// AccountEditor.jsx's USD/UYU split) pass their own key so
					// each field's id lands in its own param instead of
					// colliding on one generic 'bankAccountId'.
					target.searchParams.set(bankAccountIdKey, createdBankAccountId);
				}
				navigate(`${target.pathname}${target.search}`, { state: { newBankAccount: createdAccount } });
				return;
			}

			navigate('/AdminScreen');
		} catch (ex) {
			dispatch({ type: 'CREATE_FAIL' });
			toast.error(getError(ex));
		}
	};

	return (
		<EntityList>
			<Helmet>
				<title>Crear Cuenta Bancaria</title>
			</Helmet>
			<PageHead
				breadcrumb={[
					{ label: 'Inicio', to: '/AdminScreen' },
					{ label: 'Nueva cuenta bancaria' },
				]}
				title='Crear Cuenta Bancaria'
				subtitle='Completá los datos para registrar la cuenta bancaria.'
			/>
			<Form id='bank-account-editor-form' onSubmit={handleSubmit} className='tw:space-y-4'>
				<div className='tw:sticky tw:top-14 tw:z-20 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:rounded-panel tw:border tw:border-border tw:bg-surface/95 tw:px-5 tw:py-3.5 tw:shadow-sm tw:backdrop-blur'>
					<div className='tw:text-[12.5px] tw:text-ink-2'>
						Completá los datos principales de la cuenta bancaria.
					</div>
					<div className='tw:flex tw:gap-2'>
						<Button icon={<MdArrowBack />} onClick={() => navigate(backTo)} disabled={loadingCreate}>
							Cancelar
						</Button>
						{onReturn && (
							<Button icon={<MdKeyboardReturn />} onClick={onReturn} disabled={loadingCreate}>
								{returnLabel || 'Volver'}
							</Button>
						)}
						<Button
							variant='primary'
							icon={<MdLibraryAdd />}
							type='submit'
							disabled={loadingCreate}
						>
							{loadingCreate ? 'Guardando...' : 'Agregar'}
						</Button>
					</div>
				</div>

				<Section title='Datos de la cuenta bancaria'>
					<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
						<FormField id='bank-account-owner' label={FIELD_LABELS.owner} required>
							<Form.Control
								id='bank-account-owner'
								className={controlClass}
								type='text'
								value={owner}
								disabled={loadingCreate}
								placeholder='Ingresar propietario...'
								onChange={(event) => {
									setOwner(event.target.value);
									clearFieldError('owner');
								}}
							/>
							<FieldError message={fieldErrors.owner} />
						</FormField>
						<FormField id='bank-account-bank' label={FIELD_LABELS.bank} required>
							<Form.Control
								id='bank-account-bank'
								className={controlClass}
								type='text'
								value={bank}
								disabled={loadingCreate}
								onChange={(event) => {
									setBank(event.target.value);
									clearFieldError('bank');
								}}
							/>
							<FieldError message={fieldErrors.bank} />
						</FormField>
						<FormField id='bank-account-branch' label='Sucursal'>
							<Form.Control
								id='bank-account-branch'
								className={controlClass}
								type='text'
								value={branch}
								disabled={loadingCreate}
								onChange={(event) => setBranch(event.target.value)}
							/>
						</FormField>
						<FormField id='bank-account-type' label={FIELD_LABELS.accountType} required>
							<Select
								id='bank-account-type'
								options={accountTypeOptions}
								value={accountType}
								disabled={loadingCreate}
								placeholder='Elegí tipo de cuenta'
								onChange={(event) => {
									setAccountType(event.target.value);
									clearFieldError('accountType');
								}}
							/>
							<FieldError message={fieldErrors.accountType} />
						</FormField>
						<FormField id='bank-account-number' label={FIELD_LABELS.accountNumber} required>
							<Form.Control
								id='bank-account-number'
								className={controlClass}
								type='text'
								value={accountNumber}
								disabled={loadingCreate}
								onChange={(event) => {
									setAccountNumber(event.target.value);
									clearFieldError('accountNumber');
								}}
							/>
							<FieldError message={fieldErrors.accountNumber} />
						</FormField>
						<FormField id='bank-account-currency' label={FIELD_LABELS.currency} required>
							{isCurrencyLocked ? (
								<Form.Control id='bank-account-currency' className={controlClass} readOnly value={currency} />
							) : (
								<Select
									id='bank-account-currency'
									options={currencyOptions}
									value={currency}
									disabled={loadingCreate}
									placeholder='Elegí moneda'
									onChange={(event) => {
										setCurrency(event.target.value);
										clearFieldError('currency');
									}}
								/>
							)}
							<FieldError message={fieldErrors.currency} />
						</FormField>
					</div>
				</Section>
			</Form>
		</EntityList>
	);
}

export default NewBankAccount;
